import { useSearch } from "@tanstack/react-router";
import { Button, Switch } from "antd";
import { ChannelItemFormData, PingtreeFormData } from "components/Forms/PingtreeForm/formSchema.ts";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList.tsx";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell.ts";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useMemo } from "react";
import { FormItem } from "react-hook-form-antd";
import { CascadeAction, CascadeDistributionType } from "types/enums";
import { DrawerId } from "types/models.ts";
import { getDrawerSearchParams } from "utils/drawerHelpers";

import CascadeData = App.Data.CascadeData;

interface SearchParams {
    "cascadeDetail-id"?: string;
}

interface CascadeDetailDrawerProps {
    cascades: CascadeData[];
    onDelete: (cascadeId: string) => void;
}

export const CascadeDetailDrawer: FC<CascadeDetailDrawerProps> = ({ cascades, onDelete }) => {
    const drawerId = DrawerId.CascadeDetail;
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const cascadeId = searchParams[`${drawerId}-id`];
    const cascade = cascades.find((item) => item.id === cascadeId);
    const drawerUtils = useDrawer();
    const { t } = usePaldockTranslation("drawers.cascade-detail");
    const { t: tC } = usePaldockTranslation("common");

    if (!cascadeId) {
        return null;
    }

    const handleDelete = () => {
        if (!cascade) return;

        onDelete(cascade.id);
        drawerUtils.hide(drawerId, getDrawerSearchParams(drawerId, "id"));
    };

    return (
        <Drawer
            drawerId={drawerId}
            title={<DrawerTitle mainTitle={cascade?.title || t("title")} />}
            searchParamsToClean={getDrawerSearchParams(drawerId, "id")}
            extraButtons={
                cascade ? (
                    <Button danger size={"large"} type={"primary"} onClick={handleDelete}>
                        {tC("delete")}
                    </Button>
                ) : undefined
            }
        >
            <DrawerContent>{cascade && <CascadeDetailFormContent cascade={cascade} />}</DrawerContent>
        </Drawer>
    );
};

interface CascadeDetailFormContentProps {
    cascade: CascadeData;
}

const CascadeDetailFormContent: FC<CascadeDetailFormContentProps> = ({ cascade }) => {
    const { t } = usePaldockTranslation("drawers.cascade-detail");
    const { t: tP } = usePaldockTranslation("pages.offer.edit.pingtree.columns");
    const { t: tG } = usePaldockTranslation("general");
    const { control } = useGeneralForm<PingtreeFormData>();
    const fieldName = <K extends keyof ChannelItemFormData>(field: K) => `channels.${cascade.id}.${field}` as const;

    const distributionOptions = useMemo(
        () =>
            Object.values(CascadeDistributionType).map((value) => ({
                label: tG(`cascade_distribution_type.${value}`),
                value,
            })),
        [tG]
    );
    const actionOptions = useMemo(
        () =>
            Object.values(CascadeAction).map((value) => ({
                label: tG(`cascade_action.${value}`),
                value,
            })),
        [tG]
    );

    const list = PropertiesList.create(cascade, [
        {
            term: tP("system_name"),
            render: <InteractiveCell.Input formControl={{ control, name: fieldName("title") }} />,
        },
        {
            term: tP("visible_name"),
            render: <InteractiveCell.Input formControl={{ control, name: fieldName("visible_title") }} />,
        },
        {
            term: tP("structure"),
            description: () => cascade.integration?.form_structure?.name ?? "-",
        },
        {
            term: tP("integration"),
            render: <InteractiveCell.Integration formControl={{ control, name: fieldName("integration") }} />,
        },
        {
            term: tP("distribution"),
            render: (
                <InteractiveCell.Select
                    options={distributionOptions}
                    formControl={{ control, name: fieldName("distribution") }}
                />
            ),
        },
        {
            term: tP("redirect"),
            render: (
                <InteractiveCell.Select options={actionOptions} formControl={{ control, name: fieldName("action") }} />
            ),
        },
        {
            term: tP("min_price"),
            render: (
                <InteractiveCell.NumberInput
                    formControl={{ control, name: fieldName("bid_min_accepted") }}
                    clearable
                />
            ),
        },
        {
            term: tP("active"),
            render: (
                <FormItem control={control} name={fieldName("active")} noStyle>
                    <Switch />
                </FormItem>
            ),
        },
    ]);

    return (
        <DrawerCollapsible title={t("details")} className="m-3">
            <PropertiesList list={list} />
        </DrawerCollapsible>
    );
};
